<template>
  <div class="page-header">
    <div class="flex justify-between items-center py-4 px-6 border-b border-slate-200">
      <h1 class="text-2xl font-bold text-slate-800">{{ title }}</h1>
      
      <div class="flex items-center space-x-3">
        <!-- Notifications -->
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              v-bind="attrs" 
              v-on="on"
              class="notification-btn"
            >
              <v-badge 
                :content="unreadNotificationsCount" 
                :value="unreadNotificationsCount" 
                color="error" 
                overlap
              >
                <v-icon>mdi-bell-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>
          
          <v-card min-width="300" max-width="400" class="notification-menu">
            <v-card-title class="pb-1 d-flex justify-space-between align-center">
              <span class="text-h6">Notifications</span>
              <v-btn small text color="primary" @click="markAllAsRead">
                Mark all as read
              </v-btn>
            </v-card-title>
            
            <v-divider></v-divider>
            
            <v-list two-line max-height="400" class="overflow-y-auto">
              <template v-if="notifications.length > 0">
                <v-list-item 
                  v-for="(notification, index) in notifications" 
                  :key="index"
                  @click="readNotification(notification)"
                  :class="{'unread-notification': !notification.read}"
                >
                  <v-list-item-avatar>
                    <v-icon :color="getNotificationIconColor(notification.type)">
                      {{ getNotificationIcon(notification.type) }}
                    </v-icon>
                  </v-list-item-avatar>
                  
                  <v-list-item-content>
                    <v-list-item-title>{{ notification.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ notification.message }}</v-list-item-subtitle>
                    <span class="text-caption text-grey">{{ formatTimestamp(notification.timestamp) }}</span>
                  </v-list-item-content>
                </v-list-item>
              </template>
              
              <v-list-item v-else>
                <v-list-item-content class="text-center py-4">
                  <v-icon size="40" color="grey lighten-1">mdi-bell-sleep-outline</v-icon>
                  <div class="mt-2 text-body-1 text-grey">No notifications</div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <!-- Create New Post button -->
        <v-btn 
          color="primary" 
          class="new-post-btn"
          rounded
          @click="createNewPost"
          :disabled="isDisabled"
        >
          <v-icon left>mdi-plus</v-icon>
          New Post
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PageHeader',
  
  props: {
    title: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      notifications: [],
      unreadNotificationsCount: 0
    }
  },
  
  mounted() {
    // Here you would fetch notifications from your store or API
    this.fetchNotifications();
  },
  
  methods: {
    fetchNotifications() {
      // Example notifications - replace with actual data fetching
      this.notifications = [
        {
          id: 1,
          title: 'New post scheduled',
          message: 'Your post has been scheduled for tomorrow at 10:00 AM',
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          read: false,
          type: 'success'
        },
        {
          id: 2,
          title: 'Post published',
          message: 'Your post was successfully published on Facebook',
          timestamp: new Date(Date.now() - 86400000), // 1 day ago
          read: true,
          type: 'info'
        }
      ];
      
      this.unreadNotificationsCount = this.notifications.filter(n => !n.read).length;
    },
    
    readNotification(notification) {
      if (!notification.read) {
        notification.read = true;
        this.unreadNotificationsCount--;
        // Here you would update the read status in your backend
      }
      
      // Handle navigation or action based on notification
      this.$emit('notification-clicked', notification);
    },
    
    markAllAsRead() {
      this.notifications.forEach(notification => {
        notification.read = true;
      });
      this.unreadNotificationsCount = 0;
      // Here you would update all notifications as read in your backend
    },
    
    createNewPost() {
      this.$emit('create-post');
    },
    
    getNotificationIcon(type) {
      switch(type) {
        case 'success':
          return 'mdi-check-circle-outline';
        case 'error':
          return 'mdi-alert-circle-outline';
        case 'warning':
          return 'mdi-alert-outline';
        default:
          return 'mdi-information-outline';
      }
    },
    
    getNotificationIconColor(type) {
      switch(type) {
        case 'success':
          return 'success';
        case 'error':
          return 'error';
        case 'warning':
          return 'warning';
        default:
          return 'info';
      }
    },
    
    formatTimestamp(timestamp) {
      // Simple time formatter - could use a library like dayjs for better formatting
      const now = new Date();
      const diff = now - timestamp;
      
      // Less than a minute
      if (diff < 60000) {
        return 'Just now';
      }
      
      // Less than an hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }
      
      // Less than a day
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
      
      // Format as date
      return timestamp.toLocaleDateString();
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.notification-btn {
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.new-post-btn {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.notification-menu {
  border-radius: 8px;
  overflow: hidden;
  
  .v-list-item {
    border-left: 3px solid transparent;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.03);
    }
  }
  
  .unread-notification {
    background-color: rgba(25, 118, 210, 0.05);
    border-left-color: var(--v-primary-base);
  }
}
</style> 